import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Box, CircularProgress, Typography } from "@mui/material";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import { useSelector } from "react-redux";

const toId = (value) => String(value?._id || value?.id || value || "").trim();

/**
 * Shared dashboard-shell purchased-license display. The count comes only from
 * assigned License records (count/remainingCount), never from the legacy credit
 * wallet, because device registration consumes purchased-license count.
 */
export default function CreditBalanceIndicator() {
  const currentUser = useSelector((state) => state.auth.user);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const currentUserId = toId(currentUser);
  const [licenseSummary, setLicenseSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const refreshLicenseSummary = useCallback(async () => {
    if (!currentUser || !backendUrl) {
      setLicenseSummary(null);
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) return;

    setLoading(true);
    try {
      const response = await axios.get(`${backendUrl}/logistics/licenses`, {
        headers: { "x-auth-token": token },
      });
      const ownLicenses = (Array.isArray(response?.data) ? response.data : []).filter((license) => {
        const ownerId = toId(license?.assignedTo);
        return !currentUserId || ownerId === currentUserId;
      });
      const total = ownLicenses.reduce((sum, license) => sum + Math.max(0, Number(license?.count || 0)), 0);
      const remaining = ownLicenses.reduce(
        (sum, license) => sum + Math.max(0, Number(license?.remainingCount ?? license?.count ?? 0)),
        0
      );
      setLicenseSummary({ total, remaining, used: Math.max(0, total - remaining) });
    } catch (_) {
      // Do not show a made-up counter when the license records are unavailable.
      setLicenseSummary(null);
    } finally {
      setLoading(false);
    }
  }, [backendUrl, currentUser, currentUserId]);

  useEffect(() => {
    refreshLicenseSummary();
    window.addEventListener("licenses:changed", refreshLicenseSummary);
    const timer = window.setInterval(refreshLicenseSummary, 60000);
    return () => {
      window.removeEventListener("licenses:changed", refreshLicenseSummary);
      window.clearInterval(timer);
    };
  }, [refreshLicenseSummary]);

  if (!currentUser || (licenseSummary === null && !loading)) return null;

  return (
    <Box
      aria-live="polite"
      sx={{
        mt: 1.5,
        display: "inline-flex",
        alignItems: "center",
        gap: 1.1,
        minWidth: { xs: "100%", sm: 250 },
        px: 1.6,
        py: 1.1,
        borderRadius: 2.5,
        border: "1px solid rgba(37, 99, 235, 0.20)",
        bgcolor: "rgba(239, 246, 255, 0.92)",
        boxShadow: "0 8px 22px rgba(37, 99, 235, 0.08)",
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          bgcolor: "primary.main",
          color: "primary.contrastText",
          flexShrink: 0,
        }}
      >
        <PaidOutlinedIcon fontSize="small" />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#475569", lineHeight: 1.15 }}>
          Purchased licenses available
        </Typography>
        <Typography sx={{ mt: 0.25, fontSize: 21, fontWeight: 900, lineHeight: 1.05, color: "#0f172a" }}>
          {loading && licenseSummary === null ? <CircularProgress size={18} /> : Number(licenseSummary?.remaining || 0).toLocaleString("en-IN")}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 11, color: "#64748b", fontWeight: 700, textAlign: "right" }}>
        {licenseSummary ? `${licenseSummary.used} used of ${licenseSummary.total}` : "Loading"}
      </Typography>
    </Box>
  );
}
